import React, { FC, ReactNode } from 'react';
import { Stack } from '@mui/material';
import Button from '@ingka/button';
import Tooltip from '@ingka/tooltip';
import { colourSemanticNegative } from '@ingka/variables/colours-css';
import NegativeIcon from '../NegativeIcon';
import { ListViewItem } from '@ingka/list-view';
import useTranslation from '../../../hooks/useTranslation';
import { getIdFromLabel } from '../../../utils/utils';

export interface DirectiveCellProps {
  value: string;
  label: string;
  testId?: string;
  isMissing?: boolean;
  onEdit?: () => void;
  children?: ReactNode;
  avatar?: boolean;
  directiveDisabled?: boolean;
  optional?: boolean;
  alternativeMissingLabel?: string;
}
const returnAvatarText = (email: string) => {
  if (email && email.split(' ').length == 2) return 'MI';
  const emailArray = email && email?.split('.');
  if (emailArray?.length > 0) {
    return emailArray[0][0].toUpperCase() + emailArray[1][0].toUpperCase();
  }
  return '';
};

const DirectiveCell: FC<DirectiveCellProps> = ({
  value,
  label,
  testId,
  isMissing,
  avatar,
  onEdit,
  directiveDisabled,
  alternativeMissingLabel,
  optional,
}) => {
  const t = useTranslation();

  const avatarProps = avatar
    ? isMissing
      ? { withIcon: true, screenReaderText: label }
      : { text: returnAvatarText(value), screenReaderText: value }
    : undefined;

  return (
    <ListViewItem
      id={testId}
      {...(avatarProps ? { avatarProps } : {})}
      title={
        isMissing ? (
          <span style={{ color: colourSemanticNegative }}>
            {alternativeMissingLabel ?? 'Missing information'}
          </span>
        ) : value !== 'Missing information' ? (
          value
        ) : (
          optional && (alternativeMissingLabel ?? 'Missing information')
        )
      }
      description={label}
      quantityLabel={
        isMissing && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={3}
            sx={{
              pr: 2,
              '& .tooltip': { display: 'inline-flex', alignItems: 'center' },
            }}
          >
            <Tooltip
              tooltipText={t('labels.directiveMissingTooltip')}
              describedById={getIdFromLabel(`${label}-missing-tooltip`)}
            >
              <NegativeIcon />
            </Tooltip>
            {!directiveDisabled && (
              <Tooltip
                tooltipText={t('labels.directiveEditTooltip')}
                describedById={getIdFromLabel(`${label}-edit-tooltip`)}
              >
                <Button type="plain" size="small" onClick={onEdit}>
                  Edit
                </Button>
              </Tooltip>
            )}
          </Stack>
        )
      }
    />
  );
};

export default DirectiveCell;
